import { useContext, useEffect, useState } from 'react';
import { useLoaderData, useNavigate } from 'react-router-dom';
import { useImmerReducer } from 'use-immer';
import { GlobalContext } from '../../../library/Core/GlobalContext';
import { SmartContext } from '../../../library/Core/SmartContext';
import SessionContext from '../../../library/Core/SessionContext';
import smartReducer from '../../../library/Core/SmartReducer';
import { DispatchEvent, State } from '../../../library/Core/SmartTypes';
import DebugBarControl from '../../../library/SimpleControls/DebugBarControl';
import LoadingSpinner from '../../../library/SimpleControls/LoadingSpinner';
import { addUserSpecificDomains } from '../../../library/Service/domainService';
import AiSuggestedLayout from './AiSuggestedLayout';

const AiSuggestedDirectory = () => {
    const { globalState } = useContext(GlobalContext);
    const { sessionState } = useContext(SessionContext);
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<'approved' | 'suggested'>('suggested');

    const [state, dispatch] = useImmerReducer<State, DispatchEvent>(smartReducer, {
        flags: { isDataLoading: true, showFormErrors: 0 },
        formValidationErrors: {},
        actions: {},
    } as any);

    const { formConfig, data, internal, routeInfo } = useLoaderData() as State;
    const domain = addUserSpecificDomains(new Map([...globalState.domain]), data);

    useEffect(() => {
        if (data?.filterDomains && typeof data.filterDomains === 'object') {
            applyFilterDomains({ filterDomains: data.filterDomains, formConfig, domain });
        }

        dispatch({
            type: 'DATA_INIT',
            payload: {
                formConfig,
                data: { ...data, userTypeCode: sessionState?.userTypeCode },
                domain,
                internal,
                routeInfo,
            },
        });
    }, []);

    function applyFilterDomains({
        filterDomains,
        formConfig,
        domain,
    }: {
        filterDomains: Record<string, any[]>;
        formConfig: any;
        domain: Map<string, any[]>;
    }) {
        const allFormItems =
            formConfig?.sectionRepository?.flatMap((section: any) => section.controlGroup || []) || [];

        Object.entries(filterDomains).forEach(([key, filterValue]) => {
            if (!Array.isArray(filterValue)) return;
            const formItem = allFormItems.find((item: any) => item?.props?.domainCategoryCode === key);
            if (!formItem) {
                domain.set(key, filterValue);
                return;
            }
            domain.set(formItem.props.domainCategoryCode, filterValue);
        });
    }

    const handleTabChange = (tab: 'approved' | 'suggested') => {
        if (tab === 'approved') {
            navigate('/support/business-directory');
        }
        setActiveTab(tab);
    };

    const isAlumni = sessionState?.userTypeCode === 'ALUMNI';
    const canViewAISuggested = !isAlumni;

    if (isAlumni) {
        // Alumni cannot access AI Suggested
        navigate('/support/business-directory');
        return null;
    }

    return (
        <SmartContext.Provider value={{ state, dispatch }}>
            <div className="main flex-1">
                {!state?.data && <LoadingSpinner />}
                {state?.data && (
                    <div className="bg-white">
                        <div className="py-5">
                            <div className="max-1140 mx-auto">
                                <div className="">
                                    <div className="business-directory">
                                        <div className="row g-2">
                                            <div className="col">
                                                <h2 className="font-26 font-600 mb-0 pb-4">
                                                    {state.formConfig?.title || 'Business Directory'}
                                                </h2>
                                            </div>
                                        </div>

                                        {/* Tabs */}
                                        <div className="tabs-container mb-4">
                                            <ul
                                                className="nav nav-pills business-directory-tab mb-4 pb-2"
                                                role="tablist">
                                                <li className="nav-item" role="presentation">
                                                    <button
                                                        className={`nav-link ${activeTab === 'approved' ? 'active' : ''}`}
                                                        onClick={() => handleTabChange('approved')}
                                                        type="button"
                                                        role="tab">
                                                        Approved Directory
                                                    </button>
                                                </li>
                                                {canViewAISuggested && (
                                                    <li className="nav-item" role="presentation">
                                                        <button
                                                            className={`nav-link ${
                                                                activeTab === 'suggested' ? 'active' : ''
                                                            }`}
                                                            onClick={() => handleTabChange('suggested')}
                                                            type="button"
                                                            role="tab">
                                                            Recommended
                                                        </button>
                                                    </li>
                                                )}
                                            </ul>
                                        </div>

                                        {/* Tab Content — AI Suggested */}
                                        <AiSuggestedLayout />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <DebugBarControl />
            </div>
        </SmartContext.Provider>
    );
};

export default AiSuggestedDirectory;
